import { useCallback, useEffect, useRef, useState } from "react";

import { notify } from "../lib/telegram";
import PixelField from "./PixelField";

export interface Rest {
  left: number;
  total: number;
}

export const fmtRest = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

/**
 * Отдых между подходами.
 *
 * Отсчёт ведём от метки времени, а не вычитанием по тику: в свёрнутом WebView
 * таймеры притормаживают, и «минута» превратилась бы в полторы.
 *
 * Состояние живёт в хуке, а не в панели: отсчёт показывают двое — шапка, куда
 * смотрят между подходами, и панель внизу, где до него дотягивается палец.
 */
export function useRestTimer() {
  const [rest, setRest] = useState<Rest | null>(null);
  const deadline = useRef(0);

  const start = useCallback((seconds: number) => {
    deadline.current = Date.now() + seconds * 1000;
    setRest({ left: seconds, total: seconds });
  }, []);

  const stop = useCallback(() => setRest(null), []);

  const shift = useCallback((delta: number) => {
    deadline.current += delta * 1000;
    const left = Math.max(1, Math.ceil((deadline.current - Date.now()) / 1000));
    setRest((r) => (r ? { left, total: Math.max(r.total, left) } : r));
  }, []);

  const running = rest !== null;
  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => {
      const left = Math.ceil((deadline.current - Date.now()) / 1000);
      if (left <= 0) {
        notify("success");
        setRest(null);
        return;
      }
      setRest((r) => (r && r.left !== left ? { ...r, left } : r));
    }, 250);
    return () => clearInterval(id);
  }, [running]);

  return { rest, start, stop, shift };
}

interface Props {
  rest: Rest;
  onShift: (delta: number) => void;
  onSkip: () => void;
}

export default function RestTimer({ rest, onShift, onSkip }: Props) {
  return (
    <div className="rest">
      {/* Отдых — момент без ввода: цифры таблицы в эти минуты не читают, и
          панель может позволить себе живую матрицу. */}
      <PixelField className="rest__field" seed={5} />
      <div className="rest__bar" style={{ width: `${(rest.left / rest.total) * 100}%` }} />
      <div className="rest__body">
        <button className="rest__adj" onClick={() => onShift(-15)}>
          −15 с
        </button>
        <span className="rest__time">{fmtRest(rest.left)}</span>
        <button className="rest__adj" onClick={() => onShift(15)}>
          +15 с
        </button>
        <button className="rest__skip" onClick={onSkip}>
          Пропустить
        </button>
      </div>
    </div>
  );
}
